import { memo } from 'react';

import { CirclePlusIcon, MessageSquareIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import styles from './TileCreation.module.scss';

type SimpleTile = {
  title?: string;
  onCreateClick: () => void;
  size?: 'small' | 'medium';
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
};

type TileLimitReached = { limitReached?: boolean };

type Props = SimpleTile & TileLimitReached;

function TileCreation({
  title,
  onCreateClick,
  size,
  limitReached,
  subtitle,
  icon = <CirclePlusIcon size={16} className="text-primary" />,
  className,
}: Props) {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );

  const handleSalesClick = () => {
    if (!canReadBilling) {
      return;
    }
    navigate(`/${slug}/account/billing`, { state: { shouldTriggerAction: Date.now() } });
  };

  if (limitReached) {
    return (
      <div
        className={cn(
          styles.tile,
          styles[size],
          { [styles.isDisabled]: !canReadBilling },
          className
        )}
        data-testid="tile-creation"
      >
        <button type="button" onClick={handleSalesClick}>
          {canReadBilling && (
            <span className={cn('text-sm leading-5 font-semibold text-primary', styles.title)}>
              <MessageSquareIcon size={16} className="text-muted-foreground" />
              {t('billing.upgrade')}
            </span>
          )}
          {subtitle && (
            <span
              className={cn('text-sm leading-5 font-normal text-muted-foreground', styles.subtitle)}
            >
              {subtitle}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={cn(styles.tile, styles[size], className)} data-testid="tile-creation">
      <button type="button" onClick={onCreateClick}>
        <span className={cn('text-sm leading-5 font-semibold text-primary', styles.title)}>
          {icon}
          {title || t('common.create')}
        </span>
        {subtitle && (
          <span
            className={cn('text-sm leading-5 font-normal text-muted-foreground', styles.subtitle)}
          >
            {subtitle}
          </span>
        )}
      </button>
    </div>
  );
}

export default memo(TileCreation);
